<template>
  <div v-if="show" :class="['deadline-banner', isOverdue ? 'deadline-banner--overdue' : isPastExpected ? 'deadline-banner--grace' : isWarning ? 'deadline-banner--warning' : 'deadline-banner--ok']">
    <div class="deadline-banner__left">
      <v-icon size="18" color="inherit" class="mr-2">
        {{ isOverdue ? 'mdi-alert-circle' : isPastExpected ? 'mdi-clock-alert' : 'mdi-clock-outline' }}
      </v-icon>
      <div>
        <div class="deadline-banner__label">
          <template v-if="isOverdue">⚠️ โดนโทษปรับแล้ว (เกินเวลา)</template>
          <template v-else-if="isPastExpected">⏳ เกินเวลากำหนด — เหลือเวลาก่อนโดนปรับ</template>
          <template v-else>จะโดนปรับถ้าจบงานหลัง</template>
        </div>
        <div class="deadline-banner__time">{{ penaltyTimeStr }}</div>
        <div v-if="!isOverdue && !isPastExpected" class="deadline-banner__sub">
          (ประมาณ {{ estimatedDurationMinutes }} นาที + grace 10 นาที)
        </div>
      </div>
    </div>
    <div class="deadline-banner__right">
      <div v-if="isOverdue" class="deadline-banner__overdue-pill">
        <v-icon size="12" color="white" class="mr-1">mdi-currency-usd-off</v-icon>
        โดนปรับแล้ว
      </div>
      <div v-else class="deadline-banner__countdown" :class="isPastExpected ? 'deadline-banner__countdown--grace' : ''">
        {{ countdown }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DeadlineBanner",
  props: {
    timerStartedAt: { type: String, default: null },
    estimatedDurationMinutes: { type: Number, default: null },
  },
  data() {
    return {
      now: Date.now(),
      ticker: null,
    };
  },
  computed: {
    show() {
      return !!this.timerStartedAt && !!this.estimatedDurationMinutes;
    },
    // เวลาปกติที่ควรจบ
    expectedMs() {
      if (!this.show) return null;
      return new Date(this.timerStartedAt).getTime() + this.estimatedDurationMinutes * 60000;
    },
    // เวลา penalty จริง = +10 นาที
    penaltyMs() {
      if (!this.expectedMs) return null;
      return this.expectedMs + 10 * 60000;
    },
    remainingMs() {
      if (!this.penaltyMs) return null;
      return this.penaltyMs - this.now;
    },
    isPastExpected() {
      // เกินเวลาปกติแล้ว แต่ยังไม่โดน penalty (อยู่ใน grace 10 นาที)
      return this.expectedMs !== null && this.now > this.expectedMs && this.remainingMs > 0;
    },
    isOverdue() {
      return this.remainingMs !== null && this.remainingMs <= 0;
    },
    isWarning() {
      return !this.isPastExpected && !this.isOverdue && this.remainingMs !== null && this.remainingMs < 10 * 60000;
    },
    penaltyTimeStr() {
      if (!this.penaltyMs) return '';
      const d = new Date(this.penaltyMs);
      return d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) +
        ' น. (' + d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' }) + ')';
    },
    countdown() {
      if (this.remainingMs === null || this.isOverdue) return '';
      const total = Math.floor(this.remainingMs / 1000);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} ชม.`;
      return `${m}:${String(s).padStart(2,'0')} นาที`;
    },
  },
  mounted() {
    this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
  },
  beforeDestroy() {
    clearInterval(this.ticker);
  },
};
</script>

<style scoped>
.deadline-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 12px;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
}

.deadline-banner--ok {
  background: #f0fdf4;
  color: #166534;
  border: 1.5px solid #bbf7d0;
}

.deadline-banner--warning {
  background: #fffbeb;
  color: #92400e;
  border: 1.5px solid #fde68a;
  animation: deadline-pulse 2s ease-in-out infinite;
}

.deadline-banner--grace {
  background: #fff7ed;
  color: #9a3412;
  border: 1.5px solid #fed7aa;
  animation: deadline-pulse 1s ease-in-out infinite;
}

.deadline-banner--overdue {
  background: #fef2f2;
  color: #991b1b;
  border: 1.5px solid #fecaca;
}

@keyframes deadline-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.3); }
  50% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
}

.deadline-banner__left {
  display: flex;
  align-items: center;
}

.deadline-banner__label {
  font-size: 11px;
  font-weight: 700;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.deadline-banner__time {
  font-size: 14px;
  font-weight: 800;
  margin-top: 1px;
}

.deadline-banner__countdown {
  font-size: 18px;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.deadline-banner__countdown--grace {
  color: #ea580c;
}

.deadline-banner__sub {
  font-size: 10px;
  opacity: 0.65;
  margin-top: 2px;
}

.deadline-banner__overdue-pill {
  display: inline-flex;
  align-items: center;
  background: #dc2626;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
}
</style>
